import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import { useParticipant } from '../../hooks/useParticipant';
import type { DiscoveredTool, RegistryRow } from '../../types/aai';
import {
  Action, Banner, Chip, Divided, Drawer, EmptyState, ErrorNote, Field, Input, Kicker, Loading, Panel, PanelHead, Select, Tabs,
} from '../../components/aa';
import type { ChipTone } from '../../components/aa';
import { Tiles } from '../owner/ControlDrawers';
import { relativeTime } from '../partner/format';

/*
 * The Use Case Registry, read on the champion's Home. One inventory of every
 * approved use case, every shadow AI capture and every blocked classification,
 * read through the lens that fits the reader. The champion is the only role
 * that writes to it: scoping a record, logging shadow AI, and turning what
 * Orbit heard during onboarding into inventory rows.
 */

type LensKey = 'executive' | 'financial' | 'technical' | 'operational' | 'examiner';

interface Column { key: string; label: string }

const LENSES: Record<LensKey, { label: string; desc: string; cols: Column[] }> = {
  executive: {
    label: 'Executive',
    desc: 'Risk and value at a glance.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'dept', label: 'Team' }, { key: 'risk', label: 'Risk' },
      { key: 'value', label: 'Expected value / yr' }, { key: 'status', label: 'Status' },
    ],
  },
  financial: {
    label: 'Financial',
    desc: 'Cost against value as ranges, plus overlap with tools already paid for.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'tool', label: 'Tool' }, { key: 'cost', label: 'Cost / yr' },
      { key: 'value', label: 'Expected value / yr' }, { key: 'overlap', label: 'Overlap' },
    ],
  },
  technical: {
    label: 'Technical',
    desc: 'Which model, where the data goes, and whether a person checks the output.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'model', label: 'Model' }, { key: 'flow', label: 'Data flow' },
      { key: 'human', label: 'Human review' }, { key: 'tool', label: 'Tool' },
    ],
  },
  operational: {
    label: 'Operational',
    desc: 'Who owns each use case, what it does, and where it stands.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'dept', label: 'Team' }, { key: 'owner', label: 'Owner' },
      { key: 'capability', label: 'Capability' }, { key: 'status', label: 'Status' },
    ],
  },
  examiner: {
    label: 'Examiner',
    desc: 'The compliance record: framework mapping and who approved what.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'eu', label: 'EU AI Act' }, { key: 'nist', label: 'NIST' },
      { key: 'iso', label: 'ISO 42001' }, { key: 'risk', label: 'Risk' }, { key: 'approver', label: 'Approved by' },
    ],
  },
};
const LENS_ORDER: LensKey[] = ['executive', 'financial', 'technical', 'operational', 'examiner'];

const CAPABILITIES = ['summarization', 'drafting', 'analysis', 'automation', 'other'];
const EU_CLASSES = ['prohibited', 'high', 'limited', 'minimal'];
const NIST_FUNCTIONS = ['govern', 'map', 'measure', 'manage'];

const STATUS_TONE: Record<string, ChipTone> = { Live: 'ok', 'Shadow AI': 'warn', Blocked: 'err' };
const RISK_TONE: Record<string, ChipTone> = { high: 'err', medium: 'warn', low: 'neutral' };
const EU_TONE: Record<string, ChipTone> = { prohibited: 'err', high: 'warn', limited: 'neutral', minimal: 'neutral' };

const UNSET = new Set(['', '—', 'Not set']);
const isSet = (v?: string | null): v is string => !!v && !UNSET.has(v);

const Empty: React.FC = () => <span className="text-ink-soft">Not set</span>;

const text = (v?: string | null): React.ReactNode => {
  if (!isSet(v)) return <Empty />;
  return <span className="text-ink">{v}</span>;
};

const toneChip = (v: string | null | undefined, tones: Record<string, ChipTone>): React.ReactNode => {
  if (!isSet(v)) return <Empty />;
  return <Chip tone={tones[v] || 'neutral'} className="capitalize">{v}</Chip>;
};

const approver = (r: RegistryRow): React.ReactNode => {
  if (r.approved_by) {
    let when = '';
    if (r.approved_at) when = ` · ${relativeTime(r.approved_at)}`;
    return <span className="text-ink">{r.approved_by}{when}</span>;
  }
  if (r.status === 'Shadow AI') return <span className="text-ink-soft">Not approved</span>;
  if (r.status === 'Blocked') return <span className="text-ink-soft">Never approved</span>;
  return <Empty />;
};

const cellFor = (key: string, r: RegistryRow): React.ReactNode => {
  if (key === 'usecase') return <span className="font-bold text-ink">{r.name}</span>;
  if (key === 'dept') return text(r.department);
  if (key === 'risk') return toneChip(r.risk, RISK_TONE);
  if (key === 'value') return text(r.est_value_band);
  if (key === 'cost') return text(r.est_cost_band);
  if (key === 'status') return toneChip(r.status, STATUS_TONE);
  if (key === 'tool') return text(r.tool_vendor);
  if (key === 'overlap') {
    if (!r.overlap) return <Empty />;
    return <Chip tone="warn">Check overlap</Chip>;
  }
  if (key === 'model') return text(r.model);
  if (key === 'flow') return text(r.data_flow);
  if (key === 'human') return text(r.human_in_loop);
  if (key === 'owner') return text(r.owner);
  if (key === 'capability') return toneChip(r.capability_tag, {});
  if (key === 'eu') return toneChip(r.eu_ai_act_class, EU_TONE);
  if (key === 'nist') return toneChip(r.nist_function, {});
  if (key === 'iso') return text(r.iso_control);
  if (key === 'approver') return approver(r);
  return <Empty />;
};

const gridStyle = (n: number): React.CSSProperties => ({ gridTemplateColumns: `1.6fr repeat(${n - 1}, 1fr)` });

const DISMISS_KEY = 'aai_registry_discovery_dismissed';
const readDismissed = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]');
  } catch {
    return [];
  }
};

const mentions = (tools: DiscoveredTool[]): string => {
  const shown = tools.slice(0, 3).map((t) => {
    if (t.count === 1) return `1 person mentions ${t.tool}`;
    return `${t.count} people mention ${t.tool}`;
  });
  let more = '';
  if (tools.length > 3) more = ` and ${tools.length - 3} more`;
  return `${shown.join(', ')}${more}. Counts only, never names.`;
};

type Open = { kind: 'record'; row: RegistryRow } | { kind: 'scope'; row: RegistryRow } | { kind: 'shadow' } | null;

export const registryCounts = (rows: RegistryRow[]) => ({
  live: rows.filter((r) => r.status === 'Live').length,
  shadow: rows.filter((r) => r.status === 'Shadow AI').length,
  blocked: rows.filter((r) => r.status === 'Blocked').length,
});

export const countsLine = (rows: RegistryRow[]): string => {
  const c = registryCounts(rows);
  return `${c.live} live · ${c.shadow} shadow AI · ${c.blocked} blocked`;
};

/** The registry pages serve the Owner and the AI Champion; the eyebrow says which. */
export const useRegistryEyebrow = (): string => {
  const { context } = useParticipant();
  const clientName = localStorage.getItem('client_name') || 'Your company';
  if (context?.workspace_role === 'OWNER') return `${clientName} · Owner`;
  return 'AI Champion';
};

/** The Home summary: the head line and the route to the full registry. */
export const RegistryStrip: React.FC<{ className?: string }> = ({ className = '' }) => {
  const registry = useAsync<{ entries: RegistryRow[] }>(() => aaiApi.getRegistry(), 'Could not load the registry.');
  let desc = 'Loading the registry';
  if (registry.error) desc = registry.error;
  if (registry.data) desc = countsLine(registry.data.entries);
  return (
    <Panel className={className}>
      <PanelHead kicker="Use Case Registry" title="Every AI use case, one record" desc={desc} action={<Action size="sm" arrow to="/registry">Open registry</Action>} />
    </Panel>
  );
};

export const RegistryPanel: React.FC<{ className?: string }> = ({ className = '' }) => {
  const registry = useAsync<{ entries: RegistryRow[]; scoped_department?: string | null }>(() => aaiApi.getRegistry(), 'Could not load the registry.');
  const discovered = useAsync<{ discovered: DiscoveredTool[] }>(() => aaiApi.getDiscoveredTools(), 'Could not check what Orbit heard.');
  const [lens, setLens] = useState<LensKey>('executive');
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  const [logging, setLogging] = useState(false);
  const [open, setOpen] = useState<Open>(null);

  const rows = registry.data?.entries || [];
  const found = discovered.data?.discovered;
  const banner = useMemo(() => (found || []).filter((d) => !dismissed.includes(d.tool)), [found, dismissed]);
  const cfg = LENSES[lens];

  const reload = () => { registry.reload(); discovered.reload(); };

  const dismiss = () => {
    const next = Array.from(new Set([...dismissed, ...banner.map((b) => b.tool)]));
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    setDismissed(next);
  };

  const logDiscovered = async () => {
    setLogging(true);
    try {
      await aaiApi.logDiscoveredTools(banner.map((b) => b.tool));
      toast.success(`${banner.length} logged to the registry`);
      reload();
    } catch {
      toast.error('That did not log.');
    } finally {
      setLogging(false);
    }
  };


  let body: React.ReactNode = null;
  if (registry.loading) body = <Loading label="Loading the registry" />;
  if (registry.error) body = <ErrorNote message={registry.error} className="m-5" />;
  if (!registry.loading && !registry.error && rows.length === 0) {
    body = <EmptyState title="No use cases yet" reason="Approved requests, logged shadow AI and blocked classifications appear here." action={<Action onClick={() => setOpen({ kind: 'shadow' })}>Log shadow AI</Action>} />;
  }
  if (!registry.loading && !registry.error && rows.length > 0) {
    body = (
      <div className="overflow-x-auto">
        <div className="min-w-[860px]">
          <div style={gridStyle(cfg.cols.length)} className="grid gap-4 border-b border-line-soft bg-canvas px-6 py-3.5">
            {cfg.cols.map((c) => <div key={c.key} className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-soft">{c.label}</div>)}
          </div>
          <Divided>
            {rows.map((r) => (
              <button
                key={r.id || r.name}
                type="button"
                onClick={() => setOpen({ kind: 'record', row: r })}
                style={gridStyle(cfg.cols.length)}
                className="grid w-full items-center gap-4 px-6 py-4 text-left text-[13px] transition-colors hover:bg-canvas"
              >
                {cfg.cols.map((c) => <div key={c.key} className="min-w-0 truncate">{cellFor(c.key, r)}</div>)}
              </button>
            ))}
          </Divided>
        </div>
      </div>
    );
  }

  return (
    <Panel className={className}>
      <PanelHead
        kicker="Use Case Registry"
        title="Every AI use case, one record"
        desc={countsLine(rows)}
        action={<Action size="sm" onClick={() => setOpen({ kind: 'shadow' })}>Log shadow AI</Action>}
      />
      <div className="px-5 pb-1 md:px-6">
        <Tabs label="Registry lens" tabs={LENS_ORDER.map((k) => ({ id: k, label: LENSES[k].label }))} active={lens} onChange={(id) => setLens(id as LensKey)} className="mb-2" />
        <p className="m-0 mb-4 text-[12.5px] text-ink-dim">{cfg.desc}</p>
        {banner.length > 0 && (
          <Banner
            className="mb-4"
            tone="quiet"
            size="sm"
            kicker="Orbit heard of unregistered AI"
            title={`${banner.length} tools people named during onboarding are not in your registry`}
            desc={mentions(banner)}
            action={(
              <div className="flex items-center gap-2">
                <Action size="sm" variant="quiet" onClick={dismiss}>Dismiss</Action>
                <Action size="sm" disabled={logging} reason="Logging" onClick={logDiscovered}>Log to registry</Action>
              </div>
            )}
          />
        )}
      </div>
      {body}

      {open?.kind === 'record' && (
        <RecordDrawer row={open.row} onScope={() => setOpen({ kind: 'scope', row: open.row })} onClose={() => setOpen(null)} />
      )}
      {open?.kind === 'scope' && <ScopeDrawer row={open.row} onSaved={reload} onClose={() => setOpen(null)} />}
      {open?.kind === 'shadow' && <ShadowDrawer onSaved={reload} onClose={() => setOpen(null)} />}
    </Panel>
  );
};

interface TrailStep { tone: ChipTone; who: string; what: string }

const trailFor = (r: RegistryRow): TrailStep[] => {
  let logged = 'Logged';
  if (r.created_at) logged = `Logged ${relativeTime(r.created_at)}`;
  if (r.status === 'Blocked') return [{ tone: 'err', who: 'Classification gate', what: 'Prohibited under the EU AI Act. No approval path exists for this class.' }];
  if (r.status === 'Shadow AI') {
    return [
      { tone: 'warn', who: r.requester_email || 'Self report', what: `${logged} as inventory, awaiting classification` },
      { tone: 'warn', who: 'AI Champion', what: 'To triage: classify or evaluate the tool' },
    ];
  }
  let approved = 'Final approval recorded';
  if (r.approved_at) approved = `Final approval recorded ${relativeTime(r.approved_at)}`;
  return [
    { tone: 'ok', who: r.requester_email || 'Requester', what: `Asked ${r.created_at ? relativeTime(r.created_at) : ''}`.trim() },
    { tone: 'ok', who: 'Department head', what: 'Cleared for the company to consider' },
    { tone: 'ok', who: r.approved_by || 'AI Champion', what: approved },
  ];
};

const sourceOf = (row: RegistryRow): string => {
  if (row.source === 'shadow_ai') return 'Shadow AI capture';
  return 'Request';
};

const DOT: Record<string, string> = { ok: 'bg-ok', warn: 'bg-[#D6C400]', err: 'bg-danger', neutral: 'bg-ink-soft' };

const RecordDrawer: React.FC<{ row: RegistryRow; onScope: () => void; onClose: () => void }> = ({ row, onScope, onClose }) => {
  const navigate = useNavigate();
  const hasTool = isSet(row.tool_vendor);

  const evaluate = () => {
    onClose();
    if (row.evaluation_id) {
      navigate(`/vendors?open=${encodeURIComponent(row.evaluation_id)}`);
      return;
    }
    navigate(`/vendors?evaluate=${encodeURIComponent(row.tool_vendor)}`);
  };

  let evalLabel = `Evaluate ${row.tool_vendor}`;
  if (row.evaluation_id) evalLabel = `View evaluation of ${row.tool_vendor}`;

  const footer = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      {hasTool && <Action size="lg" variant="quiet" onClick={evaluate}>{evalLabel}</Action>}
      {row.id && <Action size="lg" onClick={onScope}>Scope this use case</Action>}
    </div>
  );

  return (
    <Drawer onClose={onClose} kicker={<Kicker>Use case record</Kicker>} badge={toneChip(row.status, STATUS_TONE)} title={row.name} sub={row.department || 'Whole company'} footer={footer}>
      {row.status === 'Blocked' && (
        <Banner className="mb-5" tone="quiet" size="sm" title="Blocked at the classification gate" desc="Classified prohibited under the EU AI Act. It cannot proceed to build." />
      )}
      <Tiles facts={[
        { label: 'Capability', value: toneChip(row.capability_tag, {}) },
        { label: 'Risk', value: toneChip(row.risk, RISK_TONE) },
        { label: 'EU AI Act class', value: toneChip(row.eu_ai_act_class, EU_TONE) },
        { label: 'NIST function', value: toneChip(row.nist_function, {}) },
        { label: 'ISO 42001', value: text(row.iso_control) },
        { label: 'Owner', value: text(row.owner) },
        { label: 'Tool', value: text(row.tool_vendor) },
        { label: 'Model', value: text(row.model) },
        { label: 'Data flow', value: text(row.data_flow) },
        { label: 'Human review', value: text(row.human_in_loop) },
        { label: 'Cost / yr', value: text(row.est_cost_band) },
        { label: 'Expected value / yr', value: text(row.est_value_band) },
        { label: 'Target phase', value: text(row.target_phase) },
        { label: 'Source', value: sourceOf(row) },
      ]} />
      {row.scope_notes && (
        <div className="mt-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Scoping notes</div>
          <p className="m-0 mt-1.5 text-[13px] leading-[21px] text-ink">{row.scope_notes}</p>
        </div>
      )}
      {row.overlap && (
        <div className="mt-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Overlap</div>
          <p className="m-0 mt-1.5 text-[13px] leading-[21px] text-ink">{row.overlap}</p>
        </div>
      )}
      <div className="mt-6 border-t border-line-soft pt-5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Approval trail</div>
        <div className="mt-3 space-y-3">
          {trailFor(row).map((t) => (
            <div key={t.who + t.what} className="flex gap-3">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT[t.tone]}`} />
              <div>
                <div className="text-[13px] font-semibold text-ink">{t.who}</div>
                <div className="text-[12.5px] text-ink-dim">{t.what}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
};

const TEXTAREA = 'w-full rounded-act border border-input bg-white px-3 py-2 text-[13px] text-ink outline-none transition-colors focus:border-ink focus:ring-[3px] focus:ring-ink/10 resize-y';

const keep = (v?: string | null): string => {
  if (!isSet(v)) return '';
  return v;
};

const ScopeDrawer: React.FC<{ row: RegistryRow; onSaved: () => void; onClose: () => void }> = ({ row, onSaved, onClose }) => {
  const [f, setF] = useState({
    owner_email: keep(row.owner),
    target_phase: keep(row.target_phase),
    capability_tag: row.capability_tag || '',
    eu_ai_act_class: row.eu_ai_act_class || '',
    nist_function: row.nist_function || '',
    iso_control: row.iso_control || '',
    tool_vendor: keep(row.tool_vendor),
    model: keep(row.model),
    human_in_loop: keep(row.human_in_loop),
    data_flow: keep(row.data_flow),
    est_cost_band: keep(row.est_cost_band),
    est_value_band: keep(row.est_value_band),
    scope_notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!row.id) return;
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v)) as Record<string, string>;
      await aaiApi.scopeRequest(row.id, body);
      setDone(true);
      toast.success('Scoping saved');
      onSaved();
    } catch (e) {
      const x = e as { response?: { data?: { detail?: string } } };
      toast.error(x.response?.data?.detail || 'Could not save the scoping.');
    } finally {
      setBusy(false);
    }
  };

  let badge = toneChip(row.status, STATUS_TONE);
  if (done) badge = <Chip tone="ok">Completed</Chip>;
  let sub = 'Assign an owner, record the classification and fill the lens fields. Ranges beat false precision. Only filled fields change.';
  if (done) sub = 'The registry now reads the fields you filled. Portfolio and Results rank on the value band.';
  let footer = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>
      <Action size="lg" onClick={save} disabled={busy} reason="Saving">Save scoping</Action>
    </div>
  );
  if (done) footer = <div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>;

  const options = (values: string[]) => (
    <>
      <option value="">Not set</option>
      {values.map((v) => <option key={v} value={v} className="capitalize">{v}</option>)}
    </>
  );

  return (
    <Drawer onClose={onClose} kicker={<Kicker>Use Case Registry</Kicker>} badge={badge} title={`Scope ${row.name}`} sub={sub} footer={footer}>
      <Tiles facts={[{ label: 'Team', value: row.department || 'Whole company' }, { label: 'Status', value: toneChip(row.status, STATUS_TONE) }]} />
      {!done && (
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <Field label="Owner (email)" htmlFor="scope-owner"><Input id="scope-owner" value={f.owner_email} onChange={set('owner_email')} /></Field>
          <Field label="Target phase" htmlFor="scope-phase"><Input id="scope-phase" value={f.target_phase} onChange={set('target_phase')} placeholder="Application" /></Field>
          <Field label="Capability" htmlFor="scope-cap"><Select id="scope-cap" value={f.capability_tag} onChange={set('capability_tag')} className="capitalize">{options(CAPABILITIES)}</Select></Field>
          <Field label="EU AI Act class" htmlFor="scope-eu"><Select id="scope-eu" value={f.eu_ai_act_class} onChange={set('eu_ai_act_class')} className="capitalize">{options(EU_CLASSES)}</Select></Field>
          <Field label="NIST function" htmlFor="scope-nist"><Select id="scope-nist" value={f.nist_function} onChange={set('nist_function')} className="capitalize">{options(NIST_FUNCTIONS)}</Select></Field>
          <Field label="ISO 42001 control" htmlFor="scope-iso"><Input id="scope-iso" value={f.iso_control} onChange={set('iso_control')} placeholder="A.6.2.2" /></Field>
          <Field label="Tool" htmlFor="scope-tool"><Input id="scope-tool" value={f.tool_vendor} onChange={set('tool_vendor')} /></Field>
          <Field label="Model" htmlFor="scope-model"><Input id="scope-model" value={f.model} onChange={set('model')} placeholder="Claude Sonnet" /></Field>
          <Field label="Human review" htmlFor="scope-human"><Input id="scope-human" value={f.human_in_loop} onChange={set('human_in_loop')} placeholder="Reviewer signs off" /></Field>
          <Field label="Data flow" htmlFor="scope-flow"><Input id="scope-flow" value={f.data_flow} onChange={set('data_flow')} placeholder="US · Anthropic · no training" /></Field>
          <Field label="Cost / yr" htmlFor="scope-cost"><Input id="scope-cost" value={f.est_cost_band} onChange={set('est_cost_band')} placeholder="$8k to $12k" /></Field>
          <Field label="Expected value / yr" htmlFor="scope-value"><Input id="scope-value" value={f.est_value_band} onChange={set('est_value_band')} placeholder="$60k to $90k" /></Field>
          <Field label="Scoping notes" htmlFor="scope-notes" className="sm:col-span-2">
            <textarea id="scope-notes" value={f.scope_notes} onChange={set('scope_notes')} rows={3} className={TEXTAREA} />
          </Field>
        </div>
      )}
    </Drawer>
  );
};

const ShadowDrawer: React.FC<{ onSaved: () => void; onClose: () => void }> = ({ onSaved, onClose }) => {
  const [title, setTitle] = useState('');
  const [tool, setTool] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await aaiApi.createRequest({ title: title.trim(), tool_vendor: tool.trim() || undefined, source: 'shadow_ai', capability_tag: 'other' });
      setDone(true);
      toast.success('Added to the registry');
      onSaved();
    } catch {
      toast.error('Could not add it.');
    } finally {
      setBusy(false);
    }
  };

  let badge = <Chip tone="warn">Shadow AI</Chip>;
  if (done) badge = <Chip tone="ok">Completed</Chip>;
  let sub = 'AI already in use without formal approval. It enters the registry as inventory to govern, not as a request to approve.';
  if (done) sub = 'It sits in the registry as Shadow AI until you classify it or evaluate the tool.';
  let footer = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>
      <Action size="lg" onClick={save} disabled={busy || !title.trim()} reason="Name what it is used for">Add to registry</Action>
    </div>
  );
  if (done) footer = <div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>;

  return (
    <Drawer onClose={onClose} kicker={<Kicker>Use Case Registry</Kicker>} badge={badge} title="Log shadow AI" sub={sub} footer={footer}>
      <Tiles facts={[{ label: 'Enters as', value: 'Shadow AI inventory' }, { label: 'Approval', value: 'Not needed to log' }]} />
      {!done && (
        <div className="mt-6 space-y-5">
          <Field label="What is it being used for?" htmlFor="shadow-title">
            <Input id="shadow-title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Drafting client emails in ChatGPT" />
          </Field>
          <Field label="Tool" htmlFor="shadow-tool">
            <Input id="shadow-tool" value={tool} onChange={(e) => setTool(e.target.value)} placeholder="ChatGPT (personal account)" />
          </Field>
        </div>
      )}
    </Drawer>
  );
};
